import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AttachPlanTaskRequestDto,
  ReorderPlanTasksRequestDto,
} from '@evimo/client/models';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { PreventiveMaintenanceService } from '~/app/services/preventive-maintenance/preventive-maintenance.service';

export function usePlanTasks(planId: string | undefined) {
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationId = user?.organizationId ?? '';
  const scopeKey = isSuperAdmin ? 'all-orgs' : organizationId;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['preventive-maintenance', 'plan-tasks', planId, scopeKey],
    queryFn: async () => {
      const res = await PreventiveMaintenanceService.listPlanTasks(planId!);
      return res.data;
    },
    enabled: (isSuperAdmin || !!organizationId) && !!planId,
  });

  const attachMutation = useMutation({
    mutationFn: (dto: AttachPlanTaskRequestDto) =>
      PreventiveMaintenanceService.attachPlanTask(planId!, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['preventive-maintenance', 'plan-tasks', planId],
      });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (dto: ReorderPlanTasksRequestDto) =>
      PreventiveMaintenanceService.reorderPlanTasks(planId!, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['preventive-maintenance', 'plan-tasks', planId],
      });
    },
  });

  return {
    planTasks: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    attachPlanTask: attachMutation.mutateAsync,
    isAttaching: attachMutation.isPending,
    reorderPlanTasks: reorderMutation.mutateAsync,
    isReordering: reorderMutation.isPending,
  };
}
